import { defineStore } from "pinia";
import api from "~/config/extensionsApi";
import { getApiAxios } from '~/helpers/index';

interface State {
  cityData: { [key: string]: any } | null;
  stateData: { [key: string]: any } | null;
  wardData: { [key: string]: any } | null;
  storelocatorData: { [key: string]: any } | null;
  loading: boolean;
  error: any | null;
}

export const useRegionStore = defineStore("region", {
  state: (): State => ({
    cityData: {},
    stateData: {},
    wardData: {},
    storelocatorData: {},
    loading: false,
    error: null,
  }),

  getters: {
    getStateData: (state) => (countryCode: string) => {
      const data = state.stateData[countryCode];
      if (!data || !Array.isArray(data)) return data;
      const hanoi = data.filter(item => item.code === 'HANOI');
      const tphcm = data.filter(item => item.code === 'THANHPHOHOCHIMINH');
      const others = data.filter(item => item.code !== 'HANOI' && item.code !== 'THANHPHOHOCHIMINH');
      return [...hanoi, ...tphcm, ...others];
    },
    getCityData: (state) => (regionId: number) => state.cityData[regionId] || null,
    getWardData: (state) => (cityId: number) => state.wardData[cityId] || null,
    getStorelocatorData: (state) => (countryCode: string) => state.storelocatorData[countryCode] || null,
    isLoading(state) {
      return state.loading;
    },
    getError(state) {
      return state.error;
    },
  },

  actions: {
    
    async fetchStateData(countryCode: string) {
      this.loading = true;
      this.error = null;
      try {
        const url = `${api.region.region_getcity}?country_id=${countryCode}`;
        const response = await getApiAxios(url);
        if (response.code === 200) {
          this.stateData[countryCode] = response.result;
        } else {
          this.stateData[countryCode] = null;
          this.error = new Error("Failed to fetch city data.");
        }
      } catch (err) {
        this.error = new Error("Unexpected error while fetching city data. Check your Network connection.");
      } finally {
        this.loading = false;
      }
    },
    async fetchCityData(regionId: number) {
      this.loading = true;
      this.error = null;
      try {
        const url = `${api.region.region_getstates}?region_id=${regionId}`;
        const response = await getApiAxios(url);
        if (response.code === 200) {
          this.cityData[regionId] = response.result;
        } else {
          this.cityData[regionId] = null;
          this.error = new Error("Failed to fetch state data.");
        }
      } catch (err) {
        this.error = new Error("Unexpected error while fetching state data. Check your Network connection.");
      } finally {
        this.loading = false;
      }
    },
    async fetchWardData(cityId: number) {
      this.loading = true;
      this.error = null;
      try {
        const url = `${api.region.region_getwards}?city_id=${cityId}`;
        const response = await getApiAxios(url);
        if (response.code === 200) {
          this.wardData[cityId] = response.result;
        } else {
          this.wardData[cityId] = null;
          this.error = new Error("Failed to fetch ward data.");
        }
      } catch (err) {
        this.error = new Error("Unexpected error while fetching ward data. Check your Network connection.");
      } finally {
        this.loading = false;
      }
    },
    async fetchStateStorelocatorData(countryCode: string) {
      this.loading = true;
      this.error = null;
      try {
        const url = `${api.storelocator.storelocator_city}?country_id=${countryCode}`;
        const response = await getApiAxios(url);
        if (response.code === 200) {
          this.storelocatorData[countryCode] = response.result;
        } else {
          this.storelocatorData[countryCode] = null;
          this.error = new Error("Failed to fetch storelocator data.");
        }
      } catch (err) {
        this.error = new Error("Unexpected error while fetching storelocator data. Check your Network connection.");
      } finally {
        this.loading = false;
      }
    },
    resetRegionData() {
      this.cityData = {};
      this.stateData = {};
      this.wardData = {};
      this.storelocatorData = {};
    },
  },
});
